import React from 'react';
import { ActivePageHeading } from './ActivePageHeading';
import { ModernButton } from '../ui/ModernButton';
import { SaveIcon, TrashIcon } from '../ui/Icons';
import { QuestionEditor } from './index';
import { computeDisplayLabels } from '../../utils/questionnairePages';
import { isConditionSource } from '../../utils/questionnaireEditorUtils';

export function DefaultQuestionsContent({
  defaultSectionTitle,
  totalFormalQuestions,
  defaultQuestionTemplates,
  selectedDefaultTemplateId,
  setSelectedDefaultTemplateId,
  loadDefaultQuestionTemplate,
  deleteDefaultQuestionTemplateById,
  defaultTemplateName,
  setDefaultTemplateName,
  saveDefaultQuestionTemplate,
  defaultFormalTemplateId,
  setFormalTemplateAsDefault,
  resetFormalTemplateDefault,
  configuredFormalTemplateName,
  defaultQuestions,
  handleDefaultDragOver,
  handleDefaultDragLeave,
  handleDefaultDrop,
  defaultDraggedIndex,
  defaultDragOverIndex,
  handleDefaultDragStart,
  handleDefaultDragEnd,
  setDefaultDraggedIndex,
  setDefaultDragOverIndex,
  setDefaultQuestionConditional,
  updateDefaultQuestion,
  removeDefaultQuestion,
  handleDefaultOptionChange,
  handleDefaultOptionScoreChange,
  addDefaultOption,
  removeDefaultOption,
  handleDefaultOptionDragStart,
  handleDefaultOptionDragOver,
  handleDefaultOptionDragEnd,
  draggedOptionIndex,
  dragOverOptionIndex,
  addDefaultQuestion,
  pageNumber,
}) {
  return (
            <div className="space-y-4">
              <ActivePageHeading
                number={pageNumber}
                title={defaultSectionTitle || 'Formale Fragen'}
                meta={`${totalFormalQuestions} Frage${totalFormalQuestions === 1 ? '' : 'n'}`}
              />
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-4">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                  <div className="flex-1 max-w-2xl">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Formale Fragen-Vorlage laden</label>
                    <div className="flex items-stretch gap-3">
                      <select
                        value={selectedDefaultTemplateId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setSelectedDefaultTemplateId(id);
                          if (id) loadDefaultQuestionTemplate(id);
                        }}
                        className="w-full p-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-lg text-sm"
                      >
                        <option value="">Vorlage für formale Fragen auswählen...</option>
                        {defaultQuestionTemplates.map((template) => {
                          const isDefaultTemplate = String(template.id) === String(defaultFormalTemplateId);
                          return (
                            <option key={template.id} value={template.id}>
                              {isDefaultTemplate ? '★ ' : ''}{template.name}{isDefaultTemplate ? ' (Standard)' : ''}
                            </option>
                          );
                        })}
                      </select>
                      <button
                        onClick={() => deleteDefaultQuestionTemplateById(selectedDefaultTemplateId)}
                        disabled={!selectedDefaultTemplateId}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center justify-center min-w-[44px]"
                        aria-label="Vorlage für formale Fragen löschen"
                        title="Vorlage für formale Fragen löschen"
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  </div>
                  <div className="flex w-full sm:w-auto min-w-0 max-w-xs">
                    <input
                      type="text"
                      value={defaultTemplateName}
                      onChange={(e) => setDefaultTemplateName(e.target.value)}
                      placeholder="Vorlagenname"
                      className="flex-1 p-2 border border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-l-lg min-w-0 focus:outline-none focus:ring-0 focus:border-green-600 text-sm"
                    />
                    <button
                      onClick={saveDefaultQuestionTemplate}
                      className="px-3 py-2 bg-slate-600 text-white rounded-r-lg hover:bg-slate-700 flex items-center justify-center whitespace-nowrap text-sm"
                      aria-label="Vorlage für formale Fragen speichern"
                      title="Vorlage für formale Fragen speichern"
                    >
                      <SaveIcon />
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <ModernButton onClick={setFormalTemplateAsDefault} variant="secondary" size="sm">
                    Als Standard setzen
                  </ModernButton>
                  <ModernButton onClick={resetFormalTemplateDefault} variant="secondary" size="sm">
                    Standard zurücksetzen
                  </ModernButton>
                  {configuredFormalTemplateName && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                      Aktuell: {configuredFormalTemplateName}
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-4">
                {defaultQuestions.length > 0 ? (() => { const defaultLabelMap = computeDisplayLabels(defaultQuestions, 0); return defaultQuestions.map((q, index) => (
                  <div
                    key={q.id}
                    data-question-id={q.id}
                    onDragOver={(e) => handleDefaultDragOver(e, index)}
                    onDragLeave={handleDefaultDragLeave}
                    onDrop={(e) => handleDefaultDrop(e, index)}
                    className={`transition-all duration-200 ${defaultDraggedIndex === index ? 'opacity-50 scale-95' : ''
                      } ${defaultDragOverIndex === index && defaultDraggedIndex !== index ? 'border-2 border-dashed border-green-400 bg-green-50 dark:bg-green-900/20' : ''
                      }`}
                  >
                    <QuestionEditor
                      question={q}
                      index={index}
                      displayLabel={defaultLabelMap[q.id]}
                      labelMap={defaultLabelMap}
                      conditionalCandidates={defaultQuestions
                        .filter((candidate) => isConditionSource(candidate) && String(candidate.id) !== String(q.id))}
                      onConditionalChange={setDefaultQuestionConditional}
                      updateQuestion={updateDefaultQuestion}
                      removeQuestion={removeDefaultQuestion}
                      handleOptionChange={handleDefaultOptionChange}
                      handleOptionScoreChange={handleDefaultOptionScoreChange}
                      addOption={addDefaultOption}
                      removeOption={removeDefaultOption}
                      isDragging={defaultDraggedIndex === index}
                      onDragStart={handleDefaultDragStart}
                      onDragEnd={handleDefaultDragEnd}
                      setDraggedIndex={setDefaultDraggedIndex}
                      setDragOverIndex={setDefaultDragOverIndex}
                      handleOptionDragStart={handleDefaultOptionDragStart}
                      handleOptionDragOver={handleDefaultOptionDragOver}
                      handleOptionDragEnd={handleDefaultOptionDragEnd}
                      draggedOptionIndex={draggedOptionIndex}
                      dragOverOptionIndex={dragOverOptionIndex}
                    />
                  </div>
                )); })() : <div className="text-center py-10 text-slate-500">Noch keine formalen Fragen hinzugefügt.</div>}
              </div>
              <div className="pt-2">
                <div className="flex flex-wrap gap-2">
                  <ModernButton onClick={() => addDefaultQuestion('text')} variant="secondary" size="sm">+ Textfrage</ModernButton>
                  <ModernButton onClick={() => addDefaultQuestion('single-choice')} variant="secondary" size="sm">+ Single-Choice</ModernButton>
                  <ModernButton onClick={() => addDefaultQuestion('multiple-choice')} variant="secondary" size="sm">+ Multiple-Choice</ModernButton>
                  <ModernButton onClick={() => addDefaultQuestion('ranking')} variant="secondary" size="sm">+ Ranking</ModernButton>
                  <ModernButton onClick={() => addDefaultQuestion('matrix')} variant="secondary" size="sm">+ Matrix</ModernButton>
                  <ModernButton onClick={() => addDefaultQuestion('combobox')} variant="secondary" size="sm">+ Combobox</ModernButton>
                  <ModernButton onClick={() => addDefaultQuestion('image')} variant="secondary" size="sm">+ Bild</ModernButton>
                </div>
              </div>
            </div>
  );
}
